// C9 — helpers puros de etiquetas y conversión (sin React) para poder probarlos.
//
// Coherencia con el diccionario de métricas v1:
//   - DN8: la semana en curso se compara a la MISMA altura de la anterior.
//   - A6: la base de producción declara lo pendiente de validar.
//   - B1 llega en minutos desde el API; la tarjeta lo muestra en horas.

import type { KpiComparativo, ResumenPendienteValidacion, ResumenSemanaRango } from 'schemas';

const FMT_DIA_SEMANA = new Intl.DateTimeFormat('es-CO', { weekday: 'short', timeZone: 'UTC' });

/** Nombre corto del día de la semana de un ISO (`lun`, `mié`…), sin puntos. */
export function diaSemanaCorto(iso: string): string {
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return '';
  return FMT_DIA_SEMANA.format(new Date(Date.UTC(y, m - 1, d)))
    .replace(/\./g, '')
    .trim();
}

/** Texto del comparativo (DN8): a la misma altura si la semana está en curso. */
export function etiquetaComparativoSemana(
  semana: Pick<ResumenSemanaRango, 'parcial' | 'desde' | 'hasta'>,
): string {
  if (!semana.parcial) return 'vs semana anterior completa';
  return `vs misma altura de la sem. anterior (${diaSemanaCorto(semana.desde)}–${diaSemanaCorto(semana.hasta)})`;
}

export type EtiquetaBase = { texto: string; tone: 'warn' | 'ok' | 'neutral' };

/** Base de producción (A1 + A6): qué parte de lo capturado sigue sin validar. */
export function etiquetaBaseProduccion(
  pend: ResumenPendienteValidacion,
  producido: number,
): EtiquetaBase {
  if (producido <= 0) return { texto: 'sin capturas en el período', tone: 'neutral' };
  // La unidad de validación es el BLOQUE (A6): un bloque de 0 paquetes sin ✓
  // sigue pendiente aunque no mueva el porcentaje. Nunca decir "todo validado"
  // mientras quede un bloque sin validar.
  if (pend.bloques > 0) {
    const bloques = `${pend.bloques} ${pend.bloques === 1 ? 'bloque' : 'bloques'}`;
    if (pend.paquetes > 0 && pend.pct !== null) {
      return { texto: `${pend.pct.toFixed(1)} % pendiente de validar (${bloques})`, tone: 'warn' };
    }
    return { texto: `${bloques} sin validar`, tone: 'warn' };
  }
  return { texto: 'todo validado', tone: 'ok' };
}

/** Convierte un KPI comparativo en minutos a horas (delta_pct no cambia). */
export function kpiMinutosAHoras(k: KpiComparativo): KpiComparativo {
  return {
    actual: k.actual / 60,
    anterior: k.anterior / 60,
    delta_abs: k.delta_abs / 60,
    delta_pct: k.delta_pct,
  };
}
